import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultIntegrationsFilter,
  integrationPageSizes,
  IntegrationSortDirection,
  IntegrationStatus,
  isIntegrationSortProperty,
  type IntegrationsFilter,
  type IntegrationSortProperty,
} from './model';

export interface IntegrationsRouteSearch {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: IntegrationSortProperty | null;
  sortDirection?: IntegrationSortDirection;
}

export function integrationsRouteSearchSchema(
  search: Record<string, unknown>,
): IntegrationsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const page = optionalPositiveInteger(search['page']);
  const parsedSize = optionalPositiveInteger(search['size']);
  const size =
    parsedSize && integrationPageSizes.includes(parsedSize as (typeof integrationPageSizes)[number])
      ? parsedSize
      : undefined;
  const statuses = optionalTrimmedString(search['statuses']);
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const sortProperty =
    rawSortProperty && isIntegrationSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(IntegrationSortDirection).includes(
    search['sortDirection'] as IntegrationSortDirection,
  )
    ? (search['sortDirection'] as IntegrationSortDirection)
    : undefined;

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseIntegrationsRouteSearch(search: IntegrationsRouteSearch): IntegrationsFilter {
  return {
    searchTerm: search.search ?? defaultIntegrationsFilter.searchTerm,
    statuses: parseStatuses(search.statuses) ?? defaultIntegrationsFilter.statuses,
    page: search.page ? search.page - 1 : defaultIntegrationsFilter.page,
    size: search.size ?? defaultIntegrationsFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultIntegrationsFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultIntegrationsFilter.sortDirection,
  };
}

export function serializeIntegrationsRouteSearch(
  filter: IntegrationsFilter,
): IntegrationsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(!sameStatuses(filter.statuses, defaultIntegrationsFilter.statuses)
      ? { statuses: filter.statuses.join(',') }
      : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultIntegrationsFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultIntegrationsFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultIntegrationsFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

const integrationStatuses: ReadonlySet<string> = new Set(Object.values(IntegrationStatus));

function parseStatuses(value: unknown) {
  if (typeof value !== 'string') return undefined;
  return value
    .split(',')
    .filter((status): status is IntegrationStatus => integrationStatuses.has(status));
}

function sameStatuses(left: readonly IntegrationStatus[], right: readonly IntegrationStatus[]) {
  const rightStatuses = new Set(right);
  return left.length === right.length && left.every((status) => rightStatuses.has(status));
}
